"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const StepNotFoundError_1 = require("../Error/StepNotFoundError");
const Mixin_1 = require("../Util/Mixin");
const IContainerInjection_1 = require("../Container/IContainerInjection");
const FacadeEvents_1 = require("./EventDefinition/FacadeEvents");
const StepEvents_1 = require("./EventDefinition/StepEvents");
const WidgetEvents_1 = require("./EventDefinition/WidgetEvents");
const WidgetFlowStates_1 = require("../Enum/WidgetFlowStates");
/**
 * Controller for widget instance.
 */
class WidgetController {
    /**
     * Creates the widget.
     *
     * @param {WidgetConfig} config
     * @param {WidgetState} state
     * @param {WidgetStepFactory} stepFactory
     * @param {WidgetStepPluginManager} stepPluginManager
     * @param {WidgetFacadeFactory} facadeFactory
     * @param {EventContainer} eventContainer
     * @param {ErrorHandler} errorHandler
     */
    constructor(config, state, stepFactory, stepPluginManager, facadeFactory, eventContainer, errorHandler) {
        /**
         * The current step.
         */
        this.currentStep = null;
        /**
         * Tracks control state of the widget flow.
         */
        this.flowState = WidgetFlowStates_1.WidgetFlowState.IDLE;
        this.config = config;
        this.state = state;
        this.stepFactory = stepFactory;
        this.stepPluginManager = stepPluginManager;
        this.facadeFactory = facadeFactory;
        this.eventContainer = eventContainer;
        this.errorHandler = errorHandler;
        this.registerEvents();
    }
    /**
     * @inheritDoc
     * @see IContainerInjection
     */
    static create(config, state, container) {
        return new this(config, state, container.get("factory.widget_step"), container.get("plugin.manager.widget_step"), container.get("factory.widget_facade"), container.get("event.container.instance"), container.get("error.handler"));
    }
    /**
     * Handles events coming from steps.
     */
    registerEvents() {
        this.eventContainer.once(FacadeEvents_1.FacadeEvents.RENDER, this.setStepWithFlow.bind(this));
        this.eventContainer.once(FacadeEvents_1.FacadeEvents.DESTROY, this.destroy.bind(this));
        this.eventContainer.on(StepEvents_1.StepEvents.STEP_FINISHED, this.setNextStep.bind(this));
        this.eventContainer.on(StepEvents_1.StepEvents.STEP_UNFINISHED, this.setPreviousStep.bind(this));
        // Pipe errors coming from events through the error handlers.
        this.eventContainer.on(WidgetEvents_1.WidgetEvents.INTERNAL_ERROR, (error) => {
            this.errorHandler.handle(error, this).catch(console.error);
        });
    }
    /**
     * Sets a concrete step and applies step flow.
     *
     * This takes in consideration skipping of steps, thus it might happen
     * that not the step requested got applied finally.
     *
     * @param stepId
     */
    setStepWithFlow(stepId) {
        // Roadblock for concurrency.
        if (this.flowState !== WidgetFlowStates_1.WidgetFlowState.IDLE) {
            return Promise.resolve(false);
        }
        this.flowState = WidgetFlowStates_1.WidgetFlowState.BLOCKED;
        stepId = stepId || (this.state.getStepId() || this.state.getStepIndex());
        let stepIndex;
        if (typeof stepId === "string") {
            stepIndex = this.stepPluginManager.getStepIndex(this.stepPluginManager.getDefinition(stepId));
        }
        else {
            stepIndex = stepId;
        }
        return this.setStep(stepIndex)
            .then((didSet) => {
            if (didSet) {
                this.eventContainer.emit(WidgetEvents_1.WidgetEvents.STEP_NEXT, this.currentStep.getDefinition().getId(), this.state.getAllCloned());
            }
            else {
                return this.setStepWithFlow(stepIndex + 1);
            }
        })
            .catch(StepNotFoundError_1.default.and(() => {
            this.eventContainer.emit(WidgetEvents_1.WidgetEvents.FINALIZED, this.state.getAll());
        }))
            .catch(this.errorHandler.handlePromised(this));
    }
    /**
     * Sets the next step if possible.
     *
     * @return {Promise<*>}
     */
    setNextStep() {
        let stepIndex = this.state.getStepIndex() + 1;
        // Roadblock for concurrency.
        if (this.flowState !== WidgetFlowStates_1.WidgetFlowState.IDLE) {
            return Promise.resolve(false);
        }
        this.flowState = WidgetFlowStates_1.WidgetFlowState.BLOCKED;
        return this.setStep(stepIndex)
            .then((didSet) => {
            if (didSet) {
                this.eventContainer.emit(WidgetEvents_1.WidgetEvents.STEP_NEXT, this.currentStep.getDefinition().getId(), this.state.getAllCloned());
            }
            else {
                this.state.setStepIndex(this.state.getStepIndex() + 1);
                return this.setNextStep();
            }
        })
            .catch(StepNotFoundError_1.default.and(() => {
            this.eventContainer.emit(WidgetEvents_1.WidgetEvents.FINALIZED, this.state.getAll());
        }))
            .catch(this.errorHandler.handlePromised(this));
    }
    /**
     * Sets the previous step if possible.
     *
     * @return {Promise<*>}
     */
    setPreviousStep() {
        let stepIndex = this.state.getStepIndex() - 1;
        // Roadblock for concurrency.
        if (this.flowState !== WidgetFlowStates_1.WidgetFlowState.IDLE) {
            return Promise.resolve(false);
        }
        this.flowState = WidgetFlowStates_1.WidgetFlowState.BLOCKED;
        return this.setStep(stepIndex)
            .then((didSet) => {
            if (didSet) {
                this.eventContainer.emit(WidgetEvents_1.WidgetEvents.STEP_BACK, this.currentStep.getDefinition().getId(), this.state.getAllCloned());
            }
            else {
                this.state.setStepIndex(this.state.getStepIndex() - 1);
                return this.setPreviousStep();
            }
        })
            .catch(this.errorHandler.handlePromised(this));
    }
    /**
     * Sets the step with given id if it does not decide to skip.
     *
     * @param {int|string|null} stepId
     * @return {Promise<boolean>}
     *   Whether the step was not skipped.
     */
    setStep(stepId = null) {
        this.flowState = WidgetFlowStates_1.WidgetFlowState.BLOCKED;
        return Promise.resolve().then(() => {
            stepId = stepId === null ?
                // Prioritize the step ID.
                (this.state.getStepId() || this.state.getStepIndex()) :
                stepId;
            if (typeof stepId === "number") {
                return this.stepFactory.createFromIndex(stepId, this.config, this.state, this.eventContainer.createContext());
            }
            return this.stepFactory.createFromId(stepId, this.config, this.state, this.eventContainer.createContext());
        }).then((newStep) => {
            this.flowState = WidgetFlowStates_1.WidgetFlowState.CHECKING_SKIP_STEP;
            return newStep.shouldApply().then((yes) => {
                if (!yes) {
                    return false;
                }
                return this.applyStep(newStep)
                    .then(() => true);
            });
        })
            .then((set) => {
            this.flowState = WidgetFlowStates_1.WidgetFlowState.IDLE;
            return set;
        })
            // Handle all but not step not found since it`s crucial for linear
            // step handling in setNextStep.
            .catch(StepNotFoundError_1.default.andNot((error) => {
            return this.errorHandler.handlePromised(this)(error);
        }));
    }
    /**
     * Applies a step instance for sure.
     *
     * @param {WidgetStepPlugin} stepInstance
     * @return {Promise<*>}
     */
    applyStep(stepInstance) {
        this.flowState = WidgetFlowStates_1.WidgetFlowState.BLOCKED;
        const stepId = stepInstance.getDefinition().getId();
        const index = this.stepPluginManager.getStepIndex(stepInstance.getDefinition());
        this.eventContainer.emit(WidgetEvents_1.WidgetEvents.STEP_LOADING, stepId, this.state.getAllCloned());
        return this.disableCurrentStep()
            .then(() => {
            this.flowState = WidgetFlowStates_1.WidgetFlowState.INITIALIZING_STEP;
            return stepInstance.initialize();
        })
            .then(() => this.removeCurrentStep())
            .then(() => {
            this.flowState = WidgetFlowStates_1.WidgetFlowState.RENDERING_STEP;
            return stepInstance.render();
        })
            .then(() => {
            this.eventContainer.emit(WidgetEvents_1.WidgetEvents.STEP_RENDERED, stepId, this.state.getAllCloned());
            this.flowState = WidgetFlowStates_1.WidgetFlowState.REGISTERING_STEP;
            return stepInstance.registerHandlers();
        })
            .then(() => {
            this.currentStep = stepInstance;
            this.state.setStepIndex(index)
                .setStepId(stepId);
            this.eventContainer.emit(WidgetEvents_1.WidgetEvents.STEP_READY, stepId, this.state.getAllCloned());
        })
            .catch(this.errorHandler.handlePromised(this))
            .then(() => this.flowState = WidgetFlowStates_1.WidgetFlowState.IDLE);
    }
    /**
     * Disables the current step if set.
     * @return {Promise<*>}
     */
    disableCurrentStep() {
        if (!this.currentStep) {
            return Promise.resolve();
        }
        this.flowState = WidgetFlowStates_1.WidgetFlowState.DISABLING_STEP;
        return this.currentStep.disable()
            .catch(this.errorHandler.handlePromised(this))
            .then(() => this.flowState = WidgetFlowStates_1.WidgetFlowState.IDLE);
    }
    /**
     * Removes the current step if set.
     * @return {Promise<*>}
     */
    removeCurrentStep() {
        if (!this.currentStep) {
            return Promise.resolve();
        }
        this.flowState = WidgetFlowStates_1.WidgetFlowState.DESTROYING_STEP;
        return this.currentStep.destroy().then(() => {
            delete this.currentStep;
            this.eventContainer.emit(WidgetEvents_1.WidgetEvents.STEP_UNLOAD, this.state.getAllCloned());
            this.state.setStepIndex(-1).setStepId("");
        })
            .catch(this.errorHandler.handlePromised(this))
            .then(() => this.flowState = WidgetFlowStates_1.WidgetFlowState.IDLE);
    }
    /**
     * Destroys the widget.
     *
     * @return {Promise}
     */
    destroy() {
        this.eventContainer.removeAllListeners();
        this.state.removeAllListeners();
        if (!this.currentStep) {
            this.flowState = WidgetFlowStates_1.WidgetFlowState.DESTROYED;
            return;
        }
        return this.removeCurrentStep()
            .then(() => this.flowState = WidgetFlowStates_1.WidgetFlowState.DESTROYED);
    }
    /**
     * Gets the current step.
     *
     * @return {WidgetStepPlugin}
     */
    getCurrentStep() {
        return this.currentStep;
    }
    /**
     * Gets the state of the widget.
     *
     * @return {WidgetState}
     */
    getState() {
        return this.state;
    }
    /**
     * Gets the configuration of the widget.
     *
     * @return {WidgetConfig}
     */
    getConfig() {
        return this.config;
    }
    /**
     * Gets the event container of the widget.
     *
     * @return {EventContainer}
     */
    getEventContainer() {
        return this.eventContainer;
    }
    /**
     * Gets the html container element.
     *
     * @return {HTMLElement | null}
     */
    getContainer() {
        return document.getElementById(this.config.getContainerId());
    }
    /**
     * Bootstraps the widget and it's facade.
     *
     * @returns {WidgetFacade}
     */
    getFacade() {
        const facade = this.facadeFactory.create(this.state, this.config, this.eventContainer.createContext());
        return facade;
    }
}
exports.default = WidgetController;
Mixin_1.default(WidgetController, IContainerInjection_1.default);
